import { AttachDocs } from './action-types';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '@codametrix/client-api';
import * as documentUtils from '../core/document-utils';
import * as objectUtils from '../core/object-utils';
import HttpMethods from '../enums/http-methods';
import * as cmxDateTime from '../core/date-time/cmx-date-time';
import { downloadDoc } from './merge-cases.actions';
import { Dictionary } from '../core/document-utils';

export const showAttachDocsModal = createAction<CMxAPI.CaseRunV2>(
  AttachDocs.SHOW_ATTACH_DOCS_MODAL
);

export const hideAttachDocsModal = createAction<void>(
  AttachDocs.HIDE_ATTACH_DOCS_MODAL
);

export const fetchAvailableDocs = createAsyncThunk<
  CMxAPI.DocumentMetadata[],
  CMxAPI.CaseRunV2,
  { rejectValue: CMxCommonApp.SubmitError }
>(AttachDocs.GET_DOCS_TO_ATTACH, async caseRun => {
  const tenantInfo = await api.requestJSON<CMxAPI.Organization>(
    `/organization/by-name/${caseRun.tenant_name}/v1`
  );
  return getDocumentsByMRN(caseRun, tenantInfo);
});

export const fetchSpecialtiesDictionary = createAsyncThunk<
  Array<CMxAPI.DocumentServiceNoteSpecialty>,
  CMxAPI.CaseRunV2,
  { rejectValue: CMxCommonApp.SubmitError }
>(AttachDocs.FETCH_SPECIALTY_DICTIONARY, async caseRun => {
  return fetchDictionary(caseRun.tenant_uid, 'Note Specialty', 'Note Specialty');
});

export const fetchNoteTypeDictionary = createAsyncThunk<
  Array<CMxAPI.DocumentServiceNoteType>,
  CMxAPI.CaseRunV2,
  { rejectValue: CMxCommonApp.SubmitError }
>(AttachDocs.FETCH_NOTE_TYPE_DICTIONARY, async caseRun => {
  return fetchDictionary(caseRun.tenant_uid, 'Note Type', 'Note Description');
});

export const downloadDocs = createAsyncThunk<
  { htmlNotes: string[]; plainTextNotes: string[]; doc: any },
  CMxAPI.DocumentMetadata,
  { rejectValue: CMxCommonApp.SubmitError }
>(AttachDocs.DOWNLOAD_DOCS, async doc => {
  const { htmlNotesResult, plainTextNotesResult } = await downloadDoc(doc);
  return {
    htmlNotes: [htmlNotesResult],
    plainTextNotes: [plainTextNotesResult],
    doc
  };
});

const fetchDictionary = async (
  activeTenantId: string,
  dictionaryName: string,
  columnName: string
): Promise<Array<any>> => {
  const searchParams = new URLSearchParams({
    tenantId: activeTenantId,
    derivedIndicator: 'true',
    effectiveDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
    dictionaryName: dictionaryName as string,
    columnName: columnName as string
  });

  const res = await api.requestJSON<{
    rowValues: Array<any>;
  }>(`/dictionary/row/unrestricted/v2?${searchParams.toString()}`, {
    method: HttpMethods.GET
  });

  return res.rowValues;
};

const getDocumentsByMRN = async (
  caseRun: CMxAPI.CaseRun,
  tenantInfo: CMxAPI.Organization
) => {
  // docs metadata filter
  const response: CMxAPI.DocumentMetadata[] = [];
  const docFilter = new URLSearchParams({
    tenantId: tenantInfo.tenantId
  });

  const documentsRequest = await api.requestJSON<CMxAPI.DocumentMetadata[]>(
    `/documents/v1?${docFilter}`,
    {
      method: HttpMethods.POST,
      body: JSON.stringify({
        patientMrn: caseRun?.patient_identifier?.number
      })
    }
  );

  const { MimeTypes } = documentUtils;
  // group documents by externalId
  const groupedByExternalId: Dictionary<CMxAPI.DocumentMetadata[]> = objectUtils.groupBy(
    documentsRequest,
    'externalId'
  );

  Object.entries(groupedByExternalId).forEach(([externalId, docs]) => {
    // group documents by mimeType
    const groupBydDocs: Dictionary<CMxAPI.DocumentMetadata[]> = objectUtils.groupBy(
      docs,
      'mimeType'
    );
    const precedence = [MimeTypes.HTML, MimeTypes.TEXT, MimeTypes.PDF];
    const doc = documentUtils.firstAvailable(groupBydDocs, precedence);
    if (doc) {
      response.push(doc);
    }
  });

  return response;
};
